import { useState, useRef, useEffect, useCallback, type KeyboardEvent, type CSSProperties } from 'react';
import clsx from 'clsx';
import { Text } from '@/components/primitives';
import { formMessages } from '@/constants';
import {
  inlineFieldOuter,
  inlineFieldLabel,
  inlineFieldBody,
  inlineFieldInputWrapper,
  inlineFieldInputWrapperDisabled,
  inlineFieldInput,
  inlineFieldCharCount,
  inlineFieldCharCountError,
  inlineFieldError,
} from './inlineTextField.css';

const VARIANT_FONT: Record<string, CSSProperties> = {
  body1:     { fontSize: '1rem',    fontWeight: undefined },
  body2:     { fontSize: '0.875rem',fontWeight: undefined },
  subtitle1: { fontSize: '1rem',    fontWeight: 500 },
  subtitle2: { fontSize: '0.875rem',fontWeight: 500 },
  h6:        { fontSize: '1.25rem', fontWeight: 500 },
};

export interface InlineTextFieldProps {
  /** Current value */
  value: string;
  /** Change handler - called on every keystroke */
  onChange: (value: string) => void;
  /** Save handler - called on blur if value changed */
  onSave?: () => void;
  /** Placeholder text when empty */
  placeholder?: string;
  /** Maximum character length */
  maxLength?: number;
  /** Error message to display */
  error?: string;
  /** Label displayed to the left of the field */
  label?: string;
  /** Whether to show as multiline */
  multiline?: boolean;
  /** Number of rows for multiline */
  rows?: number;
  /** Typography variant for text display */
  variant?: 'body1' | 'body2' | 'subtitle1' | 'subtitle2' | 'h6';
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Font weight override */
  fontWeight?: number;
}

export function InlineTextField({
  value,
  onChange,
  onSave,
  placeholder = formMessages.placeholders.clickToEdit,
  maxLength,
  error,
  label,
  multiline = false,
  rows = 1,
  variant = 'body2',
  disabled = false,
  fontWeight,
}: InlineTextFieldProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [initialValue, setInitialValue] = useState(value);
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  // Bug-hunt #72: Escape used to call onChange(initialValue) then
  // blur(). handleBlur reads the closure's stale `value` (the pre-
  // revert one) and fires onSave because `value !== initialValue` is
  // still true at that moment. This ref lets handleBlur know to skip
  // the save when the user explicitly reverted.
  const revertingRef = useRef(false);
  // F12: track whether the USER has typed during this focus session. If a
  // refetch updates `value` while focused, that's an external change — the
  // user has not edited, so onSave must not fire on blur.
  const userEditedRef = useRef(false);

  useEffect(() => {
    if (!isFocused) setInitialValue(value);
  }, [isFocused, value]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setInitialValue(value);
    userEditedRef.current = false;
  }, [value]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (revertingRef.current) {
      revertingRef.current = false;
      userEditedRef.current = false;
      return;
    }
    // F12: only save if the user actually edited during this focus session.
    // Without this guard an external prop update can flip
    // `value !== initialValue` true even though the user did nothing.
    if (userEditedRef.current && value !== initialValue && onSave) onSave();
    userEditedRef.current = false;
  }, [value, initialValue, onSave]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !multiline) {
        e.preventDefault();
        inputRef.current?.blur();
      }
      if (e.key === 'Escape') {
        revertingRef.current = true;
        userEditedRef.current = false;
        onChange(initialValue);
        inputRef.current?.blur();
      }
    },
    [multiline, onChange, initialValue]
  );

  const showCharCount = maxLength !== undefined && value.length > maxLength - 10;
  const isOverLimit = maxLength !== undefined && value.length > maxLength;

  const fontStyle: CSSProperties = {
    ...(VARIANT_FONT[variant] ?? VARIANT_FONT.body2),
    fontWeight: fontWeight ?? (VARIANT_FONT[variant]?.fontWeight),
  };

  // Bug-hunt #71: HTML5 `maxLength` only blocks typed input on some
  // browsers; paste can exceed it (browser-dependent). Truncate in the
  // onChange handler so over-length values can never escape the
  // component, regardless of how they got into the input.
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // F12: a real user keystroke arrived — mark this focus session as
      // user-edited so the save on blur is authorized.
      userEditedRef.current = true;
      const next = e.target.value;
      if (maxLength !== undefined && next.length > maxLength) {
        onChange(next.slice(0, maxLength));
      } else {
        onChange(next);
      }
    },
    [onChange, maxLength]
  );

  const sharedInputProps = {
    ref: inputRef,
    value,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    placeholder,
    disabled,
    maxLength,
    className: inlineFieldInput,
    style: fontStyle,
  };

  return (
    <div className={inlineFieldOuter}>
      {label && (
        <Text as="span" variant="caption" color="secondary" className={inlineFieldLabel}>
          {label}
        </Text>
      )}

      <div className={inlineFieldBody}>
        <div className={clsx(inlineFieldInputWrapper, disabled && inlineFieldInputWrapperDisabled)}>
          {multiline ? (
            <textarea
              {...(sharedInputProps as React.TextareaHTMLAttributes<HTMLTextAreaElement> & { ref: React.Ref<HTMLTextAreaElement> })}
              rows={rows}
              style={{ ...fontStyle, resize: 'vertical' }}
            />
          ) : (
            <input
              {...(sharedInputProps as React.InputHTMLAttributes<HTMLInputElement> & { ref: React.Ref<HTMLInputElement> })}
            />
          )}

          {showCharCount && (
            <span className={clsx(inlineFieldCharCount, isOverLimit && inlineFieldCharCountError)}>
              {value.length}/{maxLength}
            </span>
          )}
        </div>

        {error && <span className={inlineFieldError}>{error}</span>}
      </div>
    </div>
  );
}
